/**
 * @fileoverview Tenant Users Hook
 * @module hooks/useTenantUsers
 * @description Hook for managing tenant users and invites
 */

import { useState, useEffect, useCallback } from 'react';
import { usersApi } from '../services/usersApi';
import type {
  TenantUser,
  TenantUserStats,
  InviteTenantUserInput,
  UpdateTenantUserInput,
} from '../types/users';
import { toUserMessage } from '../lib/apiError';

/**
 * Hook for fetching and managing tenant users
 */
export function useTenantUsers(tenantId: string | null | undefined) {
  const [users, setUsers] = useState<TenantUser[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchUsers = useCallback(async () => {
    if (!tenantId) {
      setUsers([]);
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const data = await usersApi.getUsers(tenantId);
      setUsers(data);
    } catch (err) {
      setError(toUserMessage(err, "We couldn't load users. Please try again."));
      setUsers([]);
    } finally {
      setLoading(false);
    }
  }, [tenantId]);

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  return {
    users,
    loading,
    error,
    refetch: fetchUsers,
  };
}

/**
 * Hook for fetching tenant user statistics
 */
export function useTenantUserStats(tenantId: string | null | undefined) {
  const [stats, setStats] = useState<TenantUserStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchStats = useCallback(async () => {
    if (!tenantId) {
      setStats(null);
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const data = await usersApi.getUserStats(tenantId);
      setStats(data);
    } catch (err) {
      setError(toUserMessage(err, "We couldn't load the statistics. Please try again."));
      setStats(null);
    } finally {
      setLoading(false);
    }
  }, [tenantId]);

  useEffect(() => {
    fetchStats();
  }, [fetchStats]);

  return {
    stats,
    loading,
    error,
    refetch: fetchStats,
  };
}

/**
 * Hook for tenant user actions (invite, update, remove)
 */
export function useTenantUserActions(tenantId: string | null | undefined) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const inviteUser = useCallback(async (data: InviteTenantUserInput) => {
    if (!tenantId) {
      throw new Error('Tenant ID is required');
    }

    setLoading(true);
    setError(null);

    try {
      const result = await usersApi.inviteUser(tenantId, data);
      return result;
    } catch (err) {
      const message = toUserMessage(err, "We couldn't invite the user. Please try again.");
      setError(message);
      throw err;
    } finally {
      setLoading(false);
    }
  }, [tenantId]);

  const updateUser = useCallback(async (userId: string, data: UpdateTenantUserInput) => {
    if (!tenantId) {
      throw new Error('Tenant ID is required');
    }

    setLoading(true);
    setError(null);

    try {
      const result = await usersApi.updateUser(tenantId, userId, data);
      return result;
    } catch (err) {
      const message = toUserMessage(err, "We couldn't update the user. Please try again.");
      setError(message);
      throw err;
    } finally {
      setLoading(false);
    }
  }, [tenantId]);

  const removeUser = useCallback(async (userId: string) => {
    if (!tenantId) {
      throw new Error('Tenant ID is required');
    }

    setLoading(true);
    setError(null);

    try {
      await usersApi.removeUser(tenantId, userId);
    } catch (err) {
      const message = toUserMessage(err, "We couldn't remove the user. Please try again.");
      setError(message);
      throw err;
    } finally {
      setLoading(false);
    }
  }, [tenantId]);

  const deactivateUser = useCallback(async (userId: string) => {
    if (!tenantId) {
      throw new Error('Tenant ID is required');
    }

    setLoading(true);
    setError(null);

    try {
      await usersApi.deactivateUser(tenantId, userId);
    } catch (err) {
      const message = toUserMessage(err, "We couldn't deactivate the user. Please try again.");
      setError(message);
      throw err;
    } finally {
      setLoading(false);
    }
  }, [tenantId]);

  const reactivateUser = useCallback(async (userId: string) => {
    if (!tenantId) {
      throw new Error('Tenant ID is required');
    }

    setLoading(true);
    setError(null);

    try {
      await usersApi.reactivateUser(tenantId, userId);
    } catch (err) {
      const message = toUserMessage(err, "We couldn't reactivate the user. Please try again.");
      setError(message);
      throw err;
    } finally {
      setLoading(false);
    }
  }, [tenantId]);

  return {
    loading,
    error,
    inviteUser,
    updateUser,
    removeUser,
    deactivateUser,
    reactivateUser,
  };
}

export default useTenantUsers;
